// src/api/authInterceptor.ts

import type {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'
import { Capacitor } from '@capacitor/core'

import { tokenService } from './tokenService'
import { refreshCoordinator } from './refreshCoordinator'

import { useAuthFacade } from '@/auth/facade/authFacade'
import { appAuth } from '@/auth/platform/appAuth'

import { logger } from '@myorg/type-lib'

async function refreshWebSession(baseURL: string) {
  logger.log(
    '[Interceptor] 🌐 Web 401 → refresh session'
  )

  await axios.post(
    `${baseURL}/api/client/refresh`,
    {},
    {
      withCredentials: true,

      /**
       * refresh 自己失敗不要再進 interceptor
       */
      _skipGlobalError: true,
    }
  )
}

async function refreshAppToken(baseURL: string) {
  logger.log(
    '[Interceptor] 📱 App 401 → refresh token'
  )

  const tokens = await tokenService(baseURL)

  await appAuth.setTokens(tokens)

  return tokens
}

async function performRefresh(baseURL: string) {
  const isApp = Capacitor.isNativePlatform()

  if (isApp) {
    return refreshAppToken(baseURL)
  }

  await refreshWebSession(baseURL)

  return null
}

export function setupAuthInterceptor(
  api: AxiosInstance,
  baseURL: string
) {
  logger.log('SETUP INTERCEPTOR', baseURL)
  logger.log('AUTH INTERCEPTOR VERSION 20260622')
  /**
   * =========================================
   * 🚀 REQUEST INTERCEPTOR
   * =========================================
   */
  api.interceptors.request.use(async config => {
    const isApp = Capacitor.isNativePlatform()

    if (isApp) {
      const token = await appAuth.getAccessToken()

      if (token) {
        config.headers = config.headers ?? {}

        config.headers.Authorization =
          `Bearer ${token}`
      }

      config.withCredentials = false
    } else {
      /**
       * Web 依靠 Session Cookie
       */
      config.withCredentials = true
    }

    return config
  })

  /**
   * =========================================
   * 📥 RESPONSE INTERCEPTOR
   * =========================================
   */
  api.interceptors.response.use(
    res => res,

    async (error: AxiosError) => {
      const original =
        error.config as InternalAxiosRequestConfig

      if (!original) {
        throw error
      }

      /**
       * refresh endpoint 不處理
       */
      if (
        original._skipGlobalError ||
        original.url?.includes('/api/client/refresh')
      ) {
        throw error
      }

      /**
       * 非 401 不處理
       */
      if (error.response?.status !== 401) {
        throw error
      }

      /**
       * 同一 request 只 retry 一次
       */
      if (original._retry) {
        throw error
      }

      original._retry = true

      /**
       * =========================================
       * Single-flight refresh
       * =========================================
       */

      /**
       * 已有人 refresh
       */
      if (!refreshCoordinator.beginRefresh()) {
        logger.log(
          '[Interceptor] ⏳ waiting refresh'
        )

        await refreshCoordinator.waitForRefresh()

        return api(original)
      }

      /**
       * 我負責 refresh
       */
      try {
        const tokens =
          await performRefresh(baseURL)

        /**
         * App refresh 成功後更新 Authorization
         */
        if (tokens?.access_token) {
          original.headers =
            original.headers ?? {}

          original.headers.Authorization =
            `Bearer ${tokens.access_token}`
        }

        refreshCoordinator.resolveRefresh()

        /**
         * retry 原請求
         */
        return api(original)
      } catch (err) {
        /**
         * refresh fail
         */
        refreshCoordinator.rejectRefresh(
          err as AxiosError
        )

        logger.warn(
          '[Interceptor] Refresh failed → logout'
        )

        const auth = useAuthFacade()

        await refreshCoordinator.runLogout(
          async () => {
            await auth.logout({
              isForce: true,
            })
          }
        )

        throw err
      }
    }
  )
}